// components/StudentBalanceDetailModal.js
import React, { useState, useEffect } from 'react';
import axiosConfig from "../../../../api/axiosConfig";

const StudentBalanceDetailModal = ({ isOpen, onClose, studentInfo }) => {
  const [loading, setLoading] = useState(false);
  const [balanceDetails, setBalanceDetails] = useState(null);
  const [kursSatislar, setKursSatislar] = useState([]);
  const [odemeler, setOdemeler] = useState([]);

  useEffect(() => {
    if (isOpen && studentInfo?.id) {
      fetchBalanceDetails();
    }
  }, [isOpen, studentInfo]);

  const fetchBalanceDetails = async () => {
    setLoading(true);
    try {
      // Kurs satışlarını getir
      const kursResponse = await axiosConfig.get(`/admin/kurssatis/musteri/${studentInfo.id}`);
      const kursData = kursResponse.data || [];
      const kursIds = kursData.map(k => k.id);
      
      // Verilen dersleri getir (sadece kurs_id'si olanlar)
      const dersResponse = await axiosConfig.get(`/admin/ogretmenders/ogrenci/${studentInfo.id}`);
      const allDersData = dersResponse.data?.data || dersResponse.data || [];
      
      // Sadece kurssatis'e bağlı dersleri filtrele (kurs_id'si kursIds içinde olanlar)
      const dersData = allDersData.filter(ders => {
        // Kurs ID kontrolü - hem number hem string olabilir
        const dersKursId = parseInt(ders.kurs_id);
        return ders.kurs_id && kursIds.some(id => parseInt(id) === dersKursId);
      });
      
      setKursSatislar(dersData); // Verilen dersleri tutuyor

      // Ödemeleri getir
      const odemeResponse = await axiosConfig.get(`/admin/odemes/musteri/${studentInfo.id}`);
      const odemeData = odemeResponse.data || [];
      setOdemeler(odemeData);

      // Hesaplamaları yap - Kurs'a bağlı verilen dersler üzerinden
      let toplamDersUcreti = 0;
      let detayliDersler = [];
      let kursBazliOzet = {};

      dersData.forEach(ders => {
        const dersUcret = parseFloat(ders.ders_ucret || 0);
        const sirketPayi = parseFloat(ders.sirket_payi || 0);
        const danisman1Payi = parseFloat(ders.danisman1 || 0);
        const danisman2Payi = parseFloat(ders.danisman2 || 0);
        const danisman3Payi = parseFloat(ders.danisman3 || 0);
        
        const toplamDers = dersUcret + sirketPayi + danisman1Payi + danisman2Payi + danisman3Payi;
        toplamDersUcreti += toplamDers;

        // Kurs bazında grupla
        if (!kursBazliOzet[ders.kurs_id]) {
          const kurs = kursData.find(k => k.id === ders.kurs_id);
          kursBazliOzet[ders.kurs_id] = {
            kursAdi: kurs?.notlar || `Kurs #${ders.kurs_id}`,
            dersSayisi: 0,
            toplamUcret: 0
          };
        }
        kursBazliOzet[ders.kurs_id].dersSayisi++;
        kursBazliOzet[ders.kurs_id].toplamUcret += toplamDers;

        detayliDersler.push({
          id: ders.id,
          kurs_id: ders.kurs_id,
          tarih: ders.baslangic_tarihi || ders.created_at,
          dersUcret,
          sirketPayi,
          danisman1Payi,
          danisman2Payi,
          danisman3Payi,
          toplam: toplamDers,
          ders_sure: ders.ders_sure,
          ders_konusu: ders.ders_konusu,
          ogretmen_adi: ders.ogretmen_name || ders.ogretmen_adi,
          notlar: ders.aciklama
        });
      });

      const toplamOdeme = odemeData.reduce((sum, odeme) => sum + parseFloat(odeme.tutar || 0), 0);
      const kalanBakiye = toplamOdeme - toplamDersUcreti;

      setBalanceDetails({
        toplamDersUcreti,
        toplamOdeme,
        kalanBakiye,
        detayliDersler,
        kursBazliOzet: Object.values(kursBazliOzet),
        toplamDersSayisi: dersData.length,
        toplamKursSayisi: kursData.length
      });

    } catch (error) {
      console.error('Bakiye detayları alınırken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount || 0));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">
              💰 Bakiye Detay - {studentInfo?.name}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="p-6">
            {/* Özet Kartlar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Toplam Borç */}
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-red-600 font-semibold">Verilen Ders Borçları</span>
                  <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="text-2xl font-bold text-red-700">
                  {formatCurrency(balanceDetails?.toplamDersUcreti || 0)}
                </div>
                <div className="text-xs text-red-600 mt-1">
                  {balanceDetails?.toplamDersSayisi || 0} ders verilmiş
                </div>
              </div>

              {/* Toplam Ödeme */}
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-600 font-semibold">Toplam Ödeme</span>
                  <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                </div>
                <div className="text-2xl font-bold text-green-700">
                  {formatCurrency(balanceDetails?.toplamOdeme || 0)}
                </div>
                <div className="text-xs text-green-600 mt-1">
                  {odemeler.length} ödeme yapıldı
                </div>
              </div>

              {/* Kalan Bakiye */}
              <div className={`${balanceDetails?.kalanBakiye >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'} border-2 rounded-xl p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`${balanceDetails?.kalanBakiye >= 0 ? 'text-blue-600' : 'text-orange-600'} font-semibold`}>
                    Kalan Bakiye
                  </span>
                  <svg className={`w-6 h-6 ${balanceDetails?.kalanBakiye >= 0 ? 'text-blue-500' : 'text-orange-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className={`text-2xl font-bold ${balanceDetails?.kalanBakiye >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                  {balanceDetails?.kalanBakiye >= 0 ? '+' : ''}{formatCurrency(balanceDetails?.kalanBakiye || 0)}
                </div>
                <div className={`text-xs ${balanceDetails?.kalanBakiye >= 0 ? 'text-blue-600' : 'text-orange-600'} mt-1`}>
                  {balanceDetails?.kalanBakiye >= 0 ? '✅ Fazla ödeme var' : '⚠️ Ödeme bekliyor'}
                </div>
              </div>
            </div>

            {/* Hesaplama Formülü */}
            <div className="bg-gray-100 rounded-xl p-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Hesaplama Formülü
              </h3>
              <div className="bg-white rounded-lg p-3 font-mono text-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-600">Toplam Ödemeler:</span>
                  <span className="font-bold">{formatCurrency(balanceDetails?.toplamOdeme || 0)}</span>
                </div>
                <div className="flex items-center justify-between mb-2 border-b pb-2">
                  <span className="text-red-600">Toplam Borç:</span>
                  <span className="font-bold">- {formatCurrency(balanceDetails?.toplamDersUcreti || 0)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={balanceDetails?.kalanBakiye >= 0 ? 'text-blue-600' : 'text-orange-600'}>
                    <strong>KALAN BAKİYE:</strong>
                  </span>
                  <span className={`text-lg font-bold ${balanceDetails?.kalanBakiye >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                    {balanceDetails?.kalanBakiye >= 0 ? '+' : ''}{formatCurrency(balanceDetails?.kalanBakiye || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Kurs Bazında Özet */}
            {balanceDetails?.kursBazliOzet && balanceDetails.kursBazliOzet.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Kurs Bazında Özet
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {balanceDetails.kursBazliOzet.map((kurs, index) => (
                    <div key={index} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="font-medium text-purple-900 mb-1">{kurs.kursAdi}</div>
                      <div className="text-sm text-purple-700">
                        <div className="flex justify-between">
                          <span>Ders Sayısı:</span>
                          <span className="font-semibold">{kurs.dersSayisi}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Toplam:</span>
                          <span className="font-semibold">{formatCurrency(kurs.toplamUcret)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detaylı Tablolar */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Verilen Dersler Detay */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-red-100 text-red-600 px-2 py-1 rounded mr-2">📚</span>
                  Verilen Dersler (Borçlar)
                </h3>
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {kursSatislar.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">Verilen ders bulunamadı</div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto overflow-x-hidden relative">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Tarih</th>
                            <th className="px-3 py-2 text-left">Öğretmen</th>
                            <th className="px-3 py-2 text-left">Konu</th>
                            <th className="px-3 py-2 text-center">Süre</th>
                            <th className="px-3 py-2 text-right">
                              <span className="flex items-center justify-end">
                                Toplam
                                <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </span>
                              <div className="text-xs font-normal text-gray-500 mt-1">
                                (Ücret detayı için üzerine gelin)
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {balanceDetails?.detayliDersler.map((ders, index) => (
                            <tr key={index} className="hover:bg-gray-50 group cursor-pointer">
                              <td className="px-3 py-2 text-xs">{formatDate(ders.tarih)}</td>
                              <td className="px-3 py-2 text-xs">{ders.ogretmen_adi || '-'}</td>
                              <td className="px-3 py-2 text-xs truncate max-w-[100px]" title={ders.ders_konusu}>
                                {ders.ders_konusu || '-'}
                              </td>
                              <td className="px-3 py-2 text-center text-xs">{ders.ders_sure || '-'}dk</td>
                              <td className="px-3 py-2 text-right font-semibold text-red-600 relative">
                                {formatCurrency(ders.toplam)}
                                
                                {/* Hover'da hesaplama detayı */}
                                <div className="absolute right-0 top-full mt-1 z-10 hidden group-hover:block">
                                  <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg min-w-[250px]">
                                    <div className="font-semibold mb-2 border-b border-gray-700 pb-1">
                                      Ücret Detayı:
                                    </div>
                                    <div className="space-y-1">
                                      <div className="flex justify-between">
                                        <span>Ders Ücreti:</span>
                                        <span>{formatCurrency(ders.dersUcret)}</span>
                                      </div>
                                      {ders.sirketPayi > 0 && (
                                        <div className="flex justify-between">
                                          <span>Şirket Payı:</span>
                                          <span>+ {formatCurrency(ders.sirketPayi)}</span>
                                        </div>
                                      )}
                                      {ders.danisman1Payi > 0 && (
                                        <div className="flex justify-between">
                                          <span>1. Danışman:</span>
                                          <span>+ {formatCurrency(ders.danisman1Payi)}</span>
                                        </div>
                                      )}
                                      {ders.danisman2Payi > 0 && (
                                        <div className="flex justify-between">
                                          <span>2. Danışman:</span>
                                          <span>+ {formatCurrency(ders.danisman2Payi)}</span>
                                        </div>
                                      )}
                                      {ders.danisman3Payi > 0 && (
                                        <div className="flex justify-between">
                                          <span>3. Danışman:</span>
                                          <span>+ {formatCurrency(ders.danisman3Payi)}</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between font-semibold pt-2 border-t border-gray-700">
                                        <span>TOPLAM:</span>
                                        <span>{formatCurrency(ders.toplam)}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-red-50">
                          <tr className="font-semibold">
                            <td colSpan="4" className="px-3 py-2 text-right">TOPLAM BORÇ:</td>
                            <td className="px-3 py-2 text-right text-red-700">
                              {formatCurrency(balanceDetails?.toplamDersUcreti || 0)}
                            </td>
                          </tr>
                          {/* Hesaplama Açıklaması */}
                          <tr>
                            <td colSpan="5" className="px-3 py-2">
                              <div className="bg-white rounded p-2 text-xs text-gray-600">
                                <div className="flex items-start">
                                  <svg className="w-4 h-4 text-blue-500 mt-0.5 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <div>
                                    <strong>Nasıl hesaplanıyor?</strong>
                                    <div className="mt-1">
                                      Her ders için: <strong>Ders Ücreti + Şirket Payı + Danışman Payları</strong> toplanır.
                                      <br />
                                      Örneğin: 300₺ (ders) + 60₺ (şirket) + 10₺ (danışman) = <strong>370₺</strong>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Ödemeler Detay */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-green-100 text-green-600 px-2 py-1 rounded mr-2">💳</span>
                  Ödemeler
                </h3>
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {odemeler.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">Ödeme bulunamadı</div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Tarih</th>
                            <th className="px-3 py-2 text-left">Banka</th>
                            <th className="px-3 py-2 text-right">Tutar</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {odemeler.map((odeme) => (
                            <tr key={odeme.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2">{formatDate(odeme.tarih)}</td>
                              <td className="px-3 py-2">{odeme.banka_ismi || 'Nakit'}</td>
                              <td className="px-3 py-2 text-right font-semibold text-green-600">
                                +{formatCurrency(odeme.tutar)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-green-50 font-semibold">
                          <tr>
                            <td colSpan="2" className="px-3 py-2 text-right">TOPLAM ÖDEME:</td>
                            <td className="px-3 py-2 text-right text-green-700">
                              {formatCurrency(balanceDetails?.toplamOdeme || 0)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Açıklama */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-800">
                  <strong>Nasıl Hesaplanıyor?</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• <strong>Verilen Dersler:</strong> Gerçekten yapılan ders ücretleri + şirket payı + danışman payları</li>
                    <li>• <strong>Kalan Bakiye:</strong> Toplam ödemeler - Toplam verilen ders ücretleri</li>
                    <li>• <strong className="text-green-600">Pozitif bakiye:</strong> Öğrenci fazla ödeme yapmış</li>
                    <li>• <strong className="text-red-600">Negatif bakiye:</strong> Öğrencinin borcu var</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentBalanceDetailModal;